import classNames from 'classnames';
import { format } from 'date-fns';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useIntl } from 'react-intl';
import LabeledContainer from '../../../../common/LabeledContainer';
import { FirstCharge } from '../../../../../interfaces/merchants/products/firstCharge';
import PurchaseItemProfile from '../PurchaseItemList/PurchaseItemProfile';

import css from '../PurchaseItemList/PurchaseItemList.module.scss';

interface OwnProps {
  productImage?: string;
  merchantId?: string;
  productName?: string;
  name?: string;
  price: number;
  waitFirstAnchor: boolean;
  firstCharge: FirstCharge | null;
  productUrl?: string;
}

type Props = OwnProps;

const PurchaseSubscriptionItemList = ({
  productImage,
  merchantId,
  productName,
  waitFirstAnchor,
  firstCharge,
  price,
  productUrl,
}: Props) => {
  const classes = classNames(css.PurchaseItemList);
  const publishedAt = DateTime.local().toString();
  const intl = useIntl();

  return (
    <LabeledContainer
      label={intl.formatMessage({
        id: `purchases.purchaseItemList.title.subscription`,
      })}
      paddingTop={true}
    >
      <div className={classes}>
        <PurchaseItemProfile
          productImage={productImage}
          productName={productName}
          merchantId={merchantId}
          productUrl={productUrl}
          schemeType="subscription"
        />
        <dl className={css.PurchaseItemList__List}>
          <dt className={css.PurchaseItemList__ListTitle}>
            {intl.formatMessage({ id: 'common.monthlyAmount' })}
          </dt>
          <dd className={css.PurchaseItemList__ListData} data-style="price">
            {intl.formatMessage(
              { id: 'common.price' },
              { price: intl.formatNumber(price) },
            )}
          </dd>
        </dl>
        {firstCharge !== null && !waitFirstAnchor && (
          <div>
            <dl className={css.PurchaseItemList__List}>
              <dt className={css.PurchaseItemList__ListTitle}>
                {intl.formatMessage({
                  id: 'common.paymentDetail.paymentForCurrentMonth',
                })}
              </dt>
              <dd
                className={css.PurchaseItemList__ListData}
                data-style="table-child"
              >
                {!firstCharge.is_first_charge_pro_rata_basis ? (
                  <span>
                    {intl.formatMessage(
                      { id: 'common.price' },
                      { price: intl.formatNumber(price) },
                    )}
                  </span>
                ) : (
                  <span>
                    {intl.formatMessage(
                      { id: 'common.price' },
                      {
                        price: intl.formatNumber(
                          firstCharge.amount_user_will_pay,
                        ),
                      },
                    )}
                  </span>
                )}
                <br />
                <p className={css.PurchaseItemList__Explanation}>
                  {!firstCharge.is_first_charge_pro_rata_basis ? (
                    <span>
                      {intl.formatMessage({
                        id: 'common.purchaseExplanation.purchaseNowWithDailyRate',
                      })}
                    </span>
                  ) : (
                    <span>
                      {intl.formatMessage({
                        id: 'common.purchaseExplanation.purchaseNowWithoutDailyRate',
                      })}
                    </span>
                  )}
                </p>
              </dd>
            </dl>
            <dl className={css.PurchaseItemList__List}>
              <dt className={css.PurchaseItemList__ListTitle}>
                {intl.formatMessage({
                  id: 'common.paymentDetail.paymentDateForCurrentMonth',
                })}
              </dt>
              <dd
                className={css.PurchaseItemList__ListData}
                data-style="table-child"
              >
                {intl.formatDate(format(publishedAt), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  weekday: 'short',
                })}
              </dd>
            </dl>
          </div>
        )}
        <dl className={css.PurchaseItemList__List}>
          {!waitFirstAnchor ? (
            <dt className={css.PurchaseItemList__ListTitle}>
              {intl.formatMessage({
                id: 'common.paymentDetail.paymentDateFromNextMonth',
              })}
            </dt>
          ) : (
            <dt className={css.PurchaseItemList__ListTitle}>
              {intl.formatMessage({
                id: 'common.paymentDate',
              })}
            </dt>
          )}
          {waitFirstAnchor ? (
            <dd
              className={css.PurchaseItemList__ListData}
              data-style="table-child"
            >
              {intl.formatMessage({
                id: 'common.paymentDetail.firstDayOfEveryMonth',
              })}
              <br />
              <p className={css.PurchaseItemList__Explanation}>
                {intl.formatMessage({
                  id: 'common.purchaseExplanation.purchaseTheNextMonth',
                })}
              </p>
            </dd>
          ) : (
            <dd
              className={css.PurchaseItemList__ListData}
              data-style="table-child"
            >
              {intl.formatMessage({
                id: 'common.paymentDetail.firstDayOfEveryMonth',
              })}
            </dd>
          )}
        </dl>

        <dl className={css.PurchaseItemList__List}>
          <dt className={css.PurchaseItemList__ListTitle}>
            {intl.formatMessage({ id: 'common.paymentMethod' })}
          </dt>
          <dd
            className={css.PurchaseItemList__ListData}
            data-style="table-child"
          >
            {intl.formatMessage({ id: 'common.creditCard' })}
          </dd>
        </dl>
      </div>
    </LabeledContainer>
  );
};

export default PurchaseSubscriptionItemList;
